import { Injectable, Injector } from "@angular/core";
import { UserIntegrationResponse } from "../models/user-integration-response.model";
import { Router } from "@angular/router";
import { DwConfigService, DwMessageSeverity, DwMessagingService, DwModalButtonStandardConfigs, DwModalConfig, DwModalService, DwModalSize, DwRoutingService, DwSecurityStateService, DwSecurityUserService, getParameterByName } from "@devwareapps/devware-cap";
import { IntegrationRequestToken, IntegrationTypes } from "../models/integration-request-token.model";
import { SchoolIntegrationResponse, SchoolIntegrationResponseType } from "../models/school-integration-response.model";
import { SchoolIntegrationCompletionRequest } from "../models/school-integration-completion-request.model";
import { Observable } from "rxjs";
import { SchoolIntegrationCompletionResponse } from "../models/school-integration-completion-response.model";
import { HttpClient } from "@angular/common/http";
import { CreateSchoolModalComponent } from "../components/create-school-modal/create-school-modal.component";
import { map } from "rxjs/operators";
import IntegrationRepositoryService from "./integration-repository.service";


@Injectable({
    providedIn: 'root'
})
export default class IntegrationUtilService {

    private static lastToken: string;
    private static lastType: string;

    private static hasLoggedIn: boolean = false;
    integrationRepository: IntegrationRepositoryService;

    constructor(private router: Router,
        private dwRoutingService: DwRoutingService,
        private dwMessagingService: DwMessagingService,
        private dwModalService: DwModalService,
        private injector: Injector,
        private dwSecurityStateService: DwSecurityStateService
        //private httpClient: HttpClient
    ) {
        this.setupIntegrationToken();

        this.dwSecurityStateService.securityStateObservable.subscribe(o => {
            // Don't do anything if the user has never logged in
            if (!IntegrationUtilService.hasLoggedIn) {

                if (o.isAuthenticated) {
                    IntegrationUtilService.hasLoggedIn = true;
                }

                return;
            }

            if (!o.isAuthenticated && !o.isAuthenticating) {

                if (IntegrationUtilService.lastToken) {

                    console.log("User has logged out, clearing integration token");
                    // User is not authenticated, so we can clear the token and type
                    IntegrationUtilService.lastToken = null;
                    IntegrationUtilService.lastType = null;
                }
            }
        });
    }

    setupIntegrationToken() {
        IntegrationUtilService.lastToken = getParameterByName(this.router, "token");
        IntegrationUtilService.lastType = getParameterByName(this.router, "type");
    }

    getIntegrationTokenParam(): string {
        return IntegrationUtilService.lastToken;
    }

    getIntegrationTokenTypeParam(): string {
        return IntegrationUtilService.lastType;
    }

    getIntegrationTokenDetails(): IntegrationRequestToken {
        // Check if there is a type and token so we can pass it to the client
        const token = this.getIntegrationTokenParam();

        if (token) {
            try {
                const tokenJson = atob(token);

                const tokenDetails: IntegrationRequestToken = JSON.parse(tokenJson);

                tokenDetails.integrationType = this.getIntegrationTokenTypeParam();
                tokenDetails.token = token;
                return tokenDetails;
            } catch (e) {
                console.error(e);
            }
        }

        return null;
    }

    handleSchoolIntegrationResponse(schoolIntegrationResponseRaw: string) {
        const schoolIntegrationResponse = this.convertResponse<SchoolIntegrationResponse>(schoolIntegrationResponseRaw);

        if (!schoolIntegrationResponse) {
            return;
        }

        switch (schoolIntegrationResponse.ResponseType) {
            case SchoolIntegrationResponseType.ApproveIntegration:
                this.showApproveIntegrationDialog(schoolIntegrationResponse);

                break;
            case SchoolIntegrationResponseType.CreateNewSchool:

                this.showCreateSchoolModal(schoolIntegrationResponse);
                break;

            case SchoolIntegrationResponseType.IntegrationError:
                this.showIntegrationError(schoolIntegrationResponse);
                break;
        }
    }

    showCreateSchoolModal(schoolIntegrationResponse: SchoolIntegrationResponse) {
        let modalConfig = new DwModalConfig();

        modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;

        modalConfig.buttons[0].buttonText = 'Create School';

        modalConfig.allowFullScreen = true;
        modalConfig.modalSize = DwModalSize.medium;
        // modalConfig.customModalWidth = '400px';
        // modalConfig.customModalHeight = '300px';
        modalConfig.data = schoolIntegrationResponse;

        modalConfig.component = CreateSchoolModalComponent;

        modalConfig.title = 'Create School and Enable Integration';

        return this.dwModalService.showModal<any, any>(modalConfig)
            .pipe(map(modalResult => {
                if (!modalResult.cancelled) {

                }
            }));
    }

    showApproveIntegrationDialog(schoolIntegrationResponse: SchoolIntegrationResponse) {
        const systemName = schoolIntegrationResponse.IntegrationSystemName || 'External System';

        this.dwModalService.showConfirmDialog({
            showConfirmDialog: true,
            message: `This allows ${systemName} to link users, purchase courses and view course progress. Do you want to proceed?`,
            title: `Enable integration with ${systemName}`,
        }).subscribe((result) => {

            if (result) {
                this.integrationRepository = this.injector.get(IntegrationRepositoryService);
                const request: SchoolIntegrationCompletionRequest = {
                    FlightSchoolName: schoolIntegrationResponse.FlightSchoolName,
                    Location: schoolIntegrationResponse.Location,
                    ResponseType: SchoolIntegrationResponseType.ApproveIntegration,
                    IntegrationRequestToken: schoolIntegrationResponse.IntegrationRequestToken,
                };

                this.integrationRepository.completeSchoolIntegration(request, schoolIntegrationResponse)
                    .subscribe((response) => {
                        this.integrationRepository.handlecompleteSchoolIntegrationResponse(response, schoolIntegrationResponse);
                    });
            }
        });
    }

    showIntegrationError(schoolIntegrationResponse: SchoolIntegrationResponse) {
        this.dwModalService.showDialog(
            `Integration Error with ${schoolIntegrationResponse.IntegrationSystemName}`,
            `${schoolIntegrationResponse.ErrorMessage}`,
            [DwModalButtonStandardConfigs.instance.okButton]
        );
    }

    handleUserIntegrationResponse(userIntegrationResponseRaw: string) {
        const userIntegrationResponse = this.convertResponse<UserIntegrationResponse>(userIntegrationResponseRaw);

        if (!userIntegrationResponse) {
            return;
        }

        const systemName = userIntegrationResponse.IntegrationSystemName || 'External System';

        this.dwModalService.showConfirmDialog({
            showConfirmDialog: true,
            message: `This allows ${systemName} to access your course progress and purchase courses from their system. Do you want to proceed?`,
            title: `Grant access to ${systemName}`,
        }).subscribe((result) => {
            if (result) {
                this.dwMessagingService.showMessage({ messageBody: userIntegrationResponse.RedirectMessage, severity: DwMessageSeverity.warning });
                // window.open(tokenExchangeResponse.RedirectUrl);
                location.href = userIntegrationResponse.RedirectUrl;
            }

        });
    }

    private convertResponse<T>(responseRaw: string): T {
        if (!responseRaw) {
            return;
        }

        const responseJson = atob(responseRaw);

        const response = JSON.parse(responseJson);

        //response.Token = responseRaw;

        return response;
    }



}